/**
 * VolunteerAssignDialog — inline picker for assigning volunteers to a
 * service block. Search-driven against `/admin/worship/volunteers/search`
 * (backed by the shared admins collection). Roles fetched from
 * `/admin/worship/roles`.
 *
 * The dialog holds a local draft — on Save it PUTs to
 * `/admin/worship/services/{id}/blocks/{i}/assignments`.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, UserPlus, X, Send } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const ROLE_LABELS = {
  worship_leader: 'Worship Leader',
  band_member: 'Band Member',
  vocalist: 'Vocalist',
  av_tech: 'AV Tech',
  sound_tech: 'Sound Tech',
  media_tech: 'Media Tech',
  other: 'Other',
};

export default function VolunteerAssignDialog({
  serviceId, blockIndex, blockTitle, initialAssignments = [], onClose,
}) {
  const qc = useQueryClient();
  const [assignments, setAssignments] = useState(() => initialAssignments.map((a) => ({ ...a })));
  const [q, setQ] = useState('');
  const [role, setRole] = useState('worship_leader');

  const searchQ = useQuery({
    queryKey: ['worship-volunteers', q],
    queryFn: async () => (await axios.get(
      `${API}/api/admin/worship/volunteers/search?q=${encodeURIComponent(q)}`,
      { headers: authH() },
    )).data,
  });

  const save = useMutation({
    mutationFn: async () => (await axios.put(
      `${API}/api/admin/worship/services/${serviceId}/blocks/${blockIndex}/assignments`,
      { assignments },
      { headers: { ...authH(), 'Content-Type': 'application/json' } },
    )).data,
    onSuccess: () => {
      toast.success('Assignments saved');
      qc.invalidateQueries({ queryKey: ['worship-services'] });
      qc.invalidateQueries({ queryKey: ['worship-present', serviceId] });
      onClose?.();
    },
    onError: (e) => toast.error(`Failed: ${e?.response?.data?.detail || e.message}`),
  });

  const addPerson = (person) => {
    if (assignments.some((a) => a.person_id === person.id && a.role === role)) return;
    setAssignments((prev) => [...prev, {
      role,
      person_id: person.id,
      person_name: person.name,
      person_email: person.email,
    }]);
  };

  const removeAssignment = (i) => setAssignments((prev) => prev.filter((_, idx) => idx !== i));

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent data-testid="assign-dialog" className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl">
        <DialogHeader>
          <DialogTitle>Assign volunteers · {blockTitle}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="assign-role-select"
              className="h-9 rounded border border-slate-700 bg-slate-950 px-2 text-xs"
            >
              {Object.entries(ROLE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name or email…"
                data-testid="assign-search"
                className="pl-8 border-slate-700 bg-slate-950 h-9"
              />
            </div>
          </div>

          <div className="max-h-40 overflow-y-auto rounded border border-slate-800 bg-slate-950">
            {(searchQ.data?.items || []).slice(0, 10).map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => addPerson(p)}
                data-testid={`assign-add-${p.id}`}
                className="flex w-full items-center justify-between border-b border-slate-800 px-3 py-2 text-left text-xs hover:bg-slate-800/60"
              >
                <div>
                  <div className="text-slate-100">{p.name}</div>
                  <div className="text-[10px] text-slate-500">{p.email}</div>
                </div>
                <UserPlus className="h-3 w-3 text-emerald-400" />
              </button>
            ))}
            {searchQ.data?.items?.length === 0 && (
              <p className="p-3 text-xs text-slate-500">No matches.</p>
            )}
          </div>

          <div>
            <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">
              Assigned ({assignments.length})
            </div>
            {assignments.length === 0 && (
              <p className="text-xs text-slate-500">No one assigned yet — search above and click a name.</p>
            )}
            <div className="space-y-1">
              {assignments.map((a, i) => (
                <div
                  key={i}
                  data-testid={`assign-row-${i}`}
                  className="flex items-center justify-between rounded border border-slate-800 bg-slate-950 px-2 py-1 text-xs"
                >
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="border-emerald-500/40 text-emerald-200 text-[9px]">
                      {ROLE_LABELS[a.role] || a.role}
                    </Badge>
                    <span className="text-slate-100">{a.person_name}</span>
                    <span className="text-[10px] text-slate-500">{a.person_email}</span>
                  </div>
                  <button type="button" onClick={() => removeAssignment(i)}
                          className="text-slate-500 hover:text-rose-400">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button
              data-testid="assign-save"
              className="bg-emerald-600 hover:bg-emerald-500"
              onClick={() => save.mutate()}
              disabled={save.isPending}
            >
              {save.isPending ? 'Saving…' : 'Save Assignments'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * NotifyTeamButton — a small button that fires POST /notify to fan-out
 * in-app notifications to every assigned volunteer in a service.
 */
export function NotifyTeamButton({ serviceId, disabled }) {
  const notify = useMutation({
    mutationFn: async () => (await axios.post(
      `${API}/api/admin/worship/services/${serviceId}/notify`,
      {},
      { headers: authH() },
    )).data,
    onSuccess: (d) => toast.success(
      d.sent_count
        ? `Notified ${d.sent_count} volunteer${d.sent_count === 1 ? '' : 's'}`
        : 'No assignees to notify yet',
    ),
    onError: (e) => toast.error(`Failed: ${e?.response?.data?.detail || e.message}`),
  });
  return (
    <Button
      size="sm"
      variant="outline"
      data-testid={`notify-team-${serviceId}`}
      className="border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
      disabled={disabled || notify.isPending}
      onClick={() => notify.mutate()}
    >
      <Send className="mr-1 h-3 w-3" /> Notify Team
    </Button>
  );
}
